import { DRAWER_ANIMATION_DURATION } from "@/constants/ui"
import type { AgentRunRef } from "@/types/agent-summary"
import { Message } from "@/types/chat"
import { CollectionRef } from "@/types/collection"
import { DayObject } from "@/types/date"
import {
  Entity,
  EntityRef,
  InspirationRef,
  isEventActorRef,
  isInspirationRef,
  isPlaceRef,
  PlaceRef,
} from "@/types/entity"
import { EventActorSummary } from "@/types/event-actor"
import {
  DetailedInspiration,
  DetailedInspirationItem,
  Inspiration,
  InspirationItemSummary,
} from "@/types/inspiration"
import { ItineraryEvent } from "@/types/itinerary"
import { Mention, Place, PlaceSummary, Stats } from "@/types/place"
import { TravelerGroup, TripRef, TripTabItinerary } from "@/types/trip"
import { UserRef } from "@/types/user"
import { WebSearchResult } from "@/types/web-search-result"
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit"

import {
  eventActorToAmplitudeProperties,
  placeToAmplitudeProperties,
} from "@/lib/analytics/amplitude-properties"
import { AmplitudeEmptyValue } from "@/lib/analytics/amplitude-types"
import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { locatorForEntry } from "@/lib/detail-sheet-url"
import {
  entityIsEventActor,
  entityIsGuide,
  entityIsPlace,
  isDetailedEntity,
  isEvent,
} from "@/lib/entity-utils"
import { isEmbedded } from "@/lib/is-embedded"
import { mtLocation } from "@/lib/mt-location"
import { getDefaultEventParams, PlaceDateKind } from "@/lib/place-utils"
import { findSheetScrollViewport } from "@/lib/sheet-scroll-viewport"
import { assert } from "@/lib/utils/assertions"
import type { PlaceAddTarget } from "@/components/add-to-target-button"
import { HotelRoomsViewParams } from "../hotel-rooms-view-params"
import { messageIdForPlaceApi } from "../message-util"
import { toastManager } from "../toast"
import { chatsApi } from "./api/chats-api"
import { eventActorApi } from "./api/event-actor-api"
import { inspirationApi } from "./api/inspiration-api"
import { placeApi } from "./api/place-api"
import { tripsApi } from "./api/trips-api"
import { webEventsApi } from "./api/web-events-api"
import { selectSession } from "./auth-slice"
import { openAuthDialog } from "./auth-thunks"
import embeddedSlice from "./embedded-slice"
import mapHoverSlice from "./map-hover-slice"
import placeSlice from "./place-slice"
import { AppDispatch, RootState } from "./redux-store"
import { initTrip } from "./trip-init-thunks"
import tripSlice, { selectTripRef } from "./trip-slice"
import userSlice from "./user-slice"

let closeCleanupTimeoutId: ReturnType<typeof setTimeout> | null = null

export type EntitySheetTarget = "photos" | "reviews"
export type EntitySheetTriggerType = "click" | "auto" | "bot"

export interface EntitySheetPlaceSearchParams {
  startDate: DayObject
  endDate: DayObject
  travelers: TravelerGroup
  dateKind: PlaceDateKind
}

export type EntitySheetTrackingContext =
  | "AdminInspirationCardGrid"
  | "AdminPlaceSuggestions"
  | "BotActionBeginBooking"
  | "BotActionShowEntitySheet"
  | "AppHomeExploreCard"
  | "AppHomeWatching"
  | "AppHomeTripCard"
  | "ChatMessagePlaceCarousel"
  | "ChatMessageGuideCarousel"
  | "GuideIdeasPlaces"
  | "ChatMessagePlaceButton"
  | "ChatMessageGuideButton"
  | "ChatMessageMap"
  | "VoiceChatCaption"
  | "VoiceChatPlaceCard"
  | "VoiceChatHotelCard"
  | "UiSignalCardTitle"
  | "UiSignalCardImage"
  | "UiSignalCardViewDetailsButton"
  | "UiSignalCardBodyPlaceButton"
  | "ClientRouteHandler"
  | "CollectionSaves"
  | "CoLocatedPlacesPopover"
  | "CreatorHubInspirationCardGrid"
  | "EventActorsList"
  | "EventVenue"
  | "EventActorDetailEvents"
  | "EventsFollowUp"
  | "ExploreGuidesCarousel"
  | "ExplorePlaces"
  | "ExploreSearch"
  | "Guide"
  | "FolioDocumentSheet"
  | "GuidesThatMentionedPlace"
  | "GuideLocationPlaceHoverCard"
  | "GuidePlaceListPopover"
  | "GuidePlacePhotoViewDetails"
  | "GuidePlaceTitleViewDetails"
  | "GuidePlaceViewDetailsCTA"
  | "GuidePlaceViewDetailsMenu"
  | "GuideSaves"
  | "InspirationBlockAddGuideDialog"
  | "Map"
  | "MapHotelBookingDetails"
  | "MapDetails"
  | "MapSearchResult"
  | "MapTripBooking"
  | "MarketingHome"
  | "MentionNoteHeader"
  | "MessageInspirationButton"
  | "PlaceDetailAttraction"
  | "PlaceDetailEvent"
  | "PlaceDetailNearbySimilar"
  | "PlaceDetailNearbyHotels"
  | "PlaceDetailNearbyRestaurants"
  | "PlaceDetailNearbyToDo"
  | "PlaceSaves"
  | "ProfilePlaceHoverCard"
  | "ProfileGuide"
  | "ProfileReview"
  | "SettingsPriceAlerts"
  | "ShowChatView"
  | "TripAdvice"
  | "TripCalendarView"
  | "TripsCalendarPage"
  | "TripIdeasCard"
  | "TripItineraryDay"
  | "TripItineraryItemActionButton"
  | "TripItineraryItemViewDetails"
  | "TripMedia"
  | "TravelStoryRecommendation"
  | "AgentRunFeed"
  | "AgentStatusPanel"
  | "AudioGuidePlayer"
  | "HotelBookingOptionsDialog"
  | "MessagePlaceCardChooseRoom"
  | typeof AmplitudeEmptyValue

export interface EntityHistoryEntry {
  kind: "entity"
  entityRef: EntityRef
  /**
   * Snapshot of the entity at the time it was pushed onto the history stack.
   * Used to restore the entity on back/forward navigation. May be a summary
   * entity; the component will refetch the detailed version as needed.
   *
   * Note: the thunk also consults the RTK Query cache (to prefer a detailed
   * version if one is cached), but that cache miss is common (e.g. guides
   * fetch with a `batchSize` arg that the thunk can't reliably reproduce),
   * which is why we carry a snapshot here as the source of truth.
   */
  entity?: Entity
  scheduleStartAt?: number // for event places
  messageId?: Message["id"] // for places loaded with messageId
  priceQuoteRef?: string
  placeSearchParams?: EntitySheetPlaceSearchParams
  addTarget?: Exclude<PlaceAddTarget, "trip">
  scroll?: number
  extras?: {
    mentions: Mention[]
    stats: Stats
  }
}

export interface AgentRunReportHistoryEntry {
  kind: "agent_run_report"
  runRef: AgentRunRef
  agentName?: string
  scroll?: number
}

export interface ActionPillAnswerHistoryEntry {
  kind: "action_pill_answer"
  pillRef: string
  label?: string
  scroll?: number
}

export interface EphemeralLocationAnswerHistoryEntry {
  kind: "ephemeral_location_answer"
  answerKey: string
  locationRef: string
  ribbonRef?: string
  label: string
  scroll?: number
}

export interface EphemeralPlaceQuestionAnswerHistoryEntry {
  kind: "ephemeral_place_question"
  answerKey: string
  entityRef: string
  question: string
  label: string
  scroll?: number
}

// Another trip member's chat, read-only
export interface CollaboratorChatHistoryEntry {
  kind: "collaborator_chat"
  tripRef: TripRef
  chatId: number
  userRef: UserRef
  label: string
  scroll?: number
}

export type IdeasTarget =
  | { kind: "guide"; inspirationRef: InspirationRef }
  | { kind: "collection"; collectionRef: CollectionRef }

export function ideasTargetRef(target: IdeasTarget) {
  return target.kind === "guide" ? target.inspirationRef : target.collectionRef
}

export interface IdeasHistoryEntry {
  kind: "ideas"
  target: IdeasTarget
  label: string
  scroll?: number
}

export interface CollectionHistoryEntry {
  kind: "collection"
  collectionRef: CollectionRef
  scroll?: number
}

export interface SearchResultHistoryEntry {
  kind: "search_result"
  searchResult: WebSearchResult
  places?: PlaceSummary[]
  eventActors?: EventActorSummary[]
  messageId?: number
  webSearchResultId?: string
  scroll?: number
}

export interface TripHistoryEntry {
  kind: "trip"
  tripRef: TripRef
  scroll?: number
}

export type DetailSheetHistoryEntry =
  | EntityHistoryEntry
  | TripHistoryEntry
  | AgentRunReportHistoryEntry
  | ActionPillAnswerHistoryEntry
  | EphemeralLocationAnswerHistoryEntry
  | EphemeralPlaceQuestionAnswerHistoryEntry
  | CollaboratorChatHistoryEntry
  | IdeasHistoryEntry
  | CollectionHistoryEntry
  | SearchResultHistoryEntry

export function searchResultEntryKey(params: {
  searchResult: WebSearchResult
  webSearchResultId?: string
}): string {
  const { searchResult, webSearchResultId } = params
  return webSearchResultId ?? searchResult.ref ?? searchResult.name
}

export function historyEntryEntityRef(
  entry: DetailSheetHistoryEntry | undefined
): EntityRef | undefined {
  return entry?.kind === "entity" ? entry.entityRef : undefined
}

export function historyEntryKey(
  entry: DetailSheetHistoryEntry | undefined
): string | undefined {
  if (!entry) return undefined
  if (entry.kind === "entity") return entry.entityRef
  if (entry.kind === "trip") return entry.tripRef
  if (entry.kind === "agent_run_report") return entry.runRef
  if (entry.kind === "action_pill_answer") return entry.pillRef
  if (entry.kind === "ephemeral_location_answer") return entry.answerKey
  if (entry.kind === "ephemeral_place_question") return entry.answerKey
  if (entry.kind === "collaborator_chat") return `chat:${entry.chatId}`
  if (entry.kind === "ideas") return ideasTargetRef(entry.target)
  if (entry.kind === "collection") return entry.collectionRef
  return searchResultEntryKey(entry)
}

/** A page to open once the in-flight bot response finishes streaming */
export type DelayedPage =
  | { kind: "entity"; entity: Entity }
  | { kind: "trip"; tripRef: TripRef }

export interface EntitySheetSliceState {
  open: boolean
  expanded: boolean
  entity?: Entity
  roomsView?: HotelRoomsViewParams
  target?: EntitySheetTarget
  trigger: EntitySheetTriggerType
  itineraryEvent?: ItineraryEvent
  messageId?: Message["id"] | undefined
  scheduleStartAt?: number // for matching schedule start_at timestamp for event places
  priceQuoteRef?: string
  placeSearchParams?: EntitySheetPlaceSearchParams
  history: {
    stack: DetailSheetHistoryEntry[]
    currentIndex: number
    isNavigating: boolean
  }
  delayedPage?: DelayedPage
  entitySheetTrackingContext: EntitySheetTrackingContext
  /** Already open when the user sent their latest message, so it may be hiding the answer's map (MIN-1933) */
  predatesQuestion: boolean
}

export const detailSheetSliceInitialState: EntitySheetSliceState = {
  open: false,
  expanded: false,
  // undefined necessary to properly reset these properties
  entity: undefined,
  target: undefined,
  trigger: "click",
  itineraryEvent: undefined,
  messageId: undefined,
  scheduleStartAt: undefined,
  priceQuoteRef: undefined,
  placeSearchParams: undefined,
  history: {
    stack: [],
    currentIndex: -1,
    isNavigating: false,
  },
  entitySheetTrackingContext: AmplitudeEmptyValue,
  predatesQuestion: false,
}

const selectSliceState = (state: RootState) => state.detailSheetSlice

function pushHistoryEntry(
  state: EntitySheetSliceState,
  entry: DetailSheetHistoryEntry
) {
  if (state.history.isNavigating) return

  if (state.history.currentIndex < state.history.stack.length - 1) {
    state.history.stack = state.history.stack.slice(
      0,
      state.history.currentIndex + 1
    )
  }

  state.history.stack.push(entry)
  state.history.currentIndex = state.history.stack.length - 1
}

function addEntityToHistory(
  state: EntitySheetSliceState,
  entityRef: EntityRef,
  entity?: Entity,
  scheduleStartAt?: number,
  messageId?: Message["id"],
  priceQuoteRef?: string,
  placeSearchParams?: EntitySheetPlaceSearchParams,
  addTarget?: Exclude<PlaceAddTarget, "trip">
) {
  let effectiveScheduleStartAt = scheduleStartAt
  if (
    entity &&
    entityIsPlace(entity) &&
    effectiveScheduleStartAt === undefined &&
    isEvent(entity)
  ) {
    const defaultParams = getDefaultEventParams(entity)
    effectiveScheduleStartAt = defaultParams?.at
  }

  const currentEntry = state.history.stack[state.history.currentIndex]
  const inherited =
    currentEntry?.kind === "entity" ? currentEntry.addTarget : undefined

  pushHistoryEntry(state, {
    kind: "entity",
    entityRef,
    entity,
    scheduleStartAt: effectiveScheduleStartAt,
    messageId,
    priceQuoteRef,
    placeSearchParams,
    addTarget: addTarget ?? inherited,
  })
}

function openTripPage(state: EntitySheetSliceState, tripRef: TripRef) {
  const currentEntry = state.history.stack[state.history.currentIndex]
  const isSamePage =
    state.open &&
    currentEntry?.kind === "trip" &&
    currentEntry.tripRef === tripRef

  if (!isSamePage) {
    pushHistoryEntry(state, { kind: "trip", tripRef })
  }

  clearEntityPageState(state)
  state.open = true
  state.predatesQuestion = false
  state.trigger = "click"
}

function tripInStack(open: boolean, history: EntitySheetSliceState["history"]) {
  if (!open) return false
  for (let i = 0; i <= history.currentIndex; i++) {
    if (history.stack[i]?.kind === "trip") return true
  }
  return false
}

function clearEntityPageState(state: EntitySheetSliceState) {
  state.entity = undefined
  state.target = undefined
  state.itineraryEvent = undefined
  state.messageId = undefined
  state.scheduleStartAt = undefined
  state.priceQuoteRef = undefined
  state.placeSearchParams = undefined
  state.roomsView = undefined
}

export function snapshotSheetScroll(dispatch: AppDispatch) {
  const bodyScrollArea = findSheetScrollViewport()
  if (bodyScrollArea) {
    dispatch(updateHistoryEntry({ scroll: bodyScrollArea.scrollTop }))
  }
}

export const selectDetailSheetOpen = createSelector(
  [selectSliceState],
  (state) => state.open
)

export const selectEntitySheetEntity = createSelector(
  [selectSliceState],
  (state) => state.entity
)

export const selectEntitySheetEntityRef = createSelector(
  [selectSliceState],
  (state) => {
    if (state.entity?.entity_ref) {
      return state.entity.entity_ref
    }
    if (state.open && state.history.currentIndex >= 0) {
      return historyEntryEntityRef(
        state.history.stack[state.history.currentIndex]
      )
    }
    return undefined
  }
)

export const selectDetailSheetCurrentPage = createSelector(
  [selectSliceState],
  (state) => {
    if (state.history.currentIndex < 0) return undefined
    return state.history.stack[state.history.currentIndex]
  }
)

export const selectDetailSheetUrlLocator = createSelector(
  [selectDetailSheetCurrentPage, selectEntitySheetEntityRef],
  (page, entityRef) => {
    if (page) {
      const locator = locatorForEntry(page)
      if (locator) return locator
    }
    if (entityRef) {
      return { ref: entityRef }
    }
    return undefined
  }
)

export const selectDetailSheetHistory = createSelector(
  [selectSliceState],
  (state) => state.history
)

export const selectDetailSheetCanGoBack = createSelector(
  [selectDetailSheetHistory],
  (history) => history.currentIndex > 0
)

/** The trip is the page currently on screen in the detail sheet */
export const selectTripPageActive = createSelector(
  [selectDetailSheetOpen, selectDetailSheetHistory],
  (open, history) =>
    open && history.stack[history.currentIndex]?.kind === "trip"
)

/** The Ask AI ideas page is the page currently on screen in the detail sheet */
export const selectIdeasPageActive = createSelector(
  [selectDetailSheetOpen, selectDetailSheetCurrentPage],
  (open, page) => open && page?.kind === "ideas"
)

/** The detail sheet is showing something other than a trip. Dialogs rendered
 *  beneath the sheet (trip page or standalone trip view) hide behind it. */
export const selectNonTripPageActive = createSelector(
  [selectDetailSheetOpen, selectDetailSheetCurrentPage],
  (open, page) => open && page?.kind !== "trip"
)

export const selectSheetCoversSearch = createSelector(
  [selectDetailSheetOpen, selectDetailSheetCurrentPage],
  (open, page) => open && page?.kind !== "trip" && page?.kind !== "ideas"
)

/** The user is working inside a trip, which may be under the current page */
export const selectTripInSheetStack = createSelector(
  [selectDetailSheetOpen, selectDetailSheetHistory],
  tripInStack
)

/** The trip of the answer page on screen, or under the place pages opened from it */
export function selectAnswerTripRefInSheetStack(
  state: RootState
): TripRef | undefined {
  const { open, history } = selectSliceState(state)
  if (!open) return undefined
  for (let i = history.currentIndex; i >= 0; i--) {
    const entry = history.stack[i]
    switch (entry?.kind) {
      case "entity":
        continue
      case "action_pill_answer":
        return chatsApi.endpoints.getActionPillAnswer.select(entry.pillRef)(
          state
        ).data?.trip_ref
      case "ephemeral_location_answer":
        return chatsApi.endpoints.getEphemeralLocationAnswer.select({
          locationRef: entry.locationRef,
          ribbonRef: entry.ribbonRef,
        })(state).data?.trip_ref
      case "ephemeral_place_question":
        return chatsApi.endpoints.getEphemeralPlaceQuestionAnswer.select({
          entityRef: entry.entityRef,
          question: entry.question,
          label: entry.label,
        })(state).data?.trip_ref
      default:
        return undefined
    }
  }
  return undefined
}

export const selectSheetPlaceAddTarget = createSelector(
  [selectDetailSheetCurrentPage],
  (page): PlaceAddTarget =>
    (page?.kind === "entity" && page.addTarget) || "trip"
)

export const selectEntitySheetTrigger = createSelector(
  [selectSliceState],
  (state) => state.trigger
)

export const selectDetailSheetPredatesQuestion = createSelector(
  [selectSliceState],
  (state) => state.predatesQuestion
)

export const selectEntitySheetTarget = createSelector(
  [selectSliceState],
  (state) => state.target
)

export const selectEntitySheetItineraryEvent = createSelector(
  [selectSliceState],
  (state) => state.itineraryEvent
)

export const selectEntitySheetMessageId = createSelector(
  [selectSliceState],
  (state) => state.messageId
)

export const selectEntitySheetScheduleStartAt = createSelector(
  [selectSliceState],
  (state) => state.scheduleStartAt
)

export const selectEntitySheetPriceQuoteRef = createSelector(
  [selectSliceState],
  (state) => state.priceQuoteRef
)

export const selectEntitySheetPlaceSearchParams = createSelector(
  [selectSliceState],
  (state) => state.placeSearchParams
)

export const selectDetailSheetExpanded = createSelector(
  [selectSliceState],
  (state) => state.expanded
)

export const selectEntitySheetRoomsView = createSelector(
  [selectSliceState],
  (state) => state.roomsView
)

export const selectEntitySheetTrackingContext = createSelector(
  [selectSliceState],
  (state) => state.entitySheetTrackingContext
)

const detailSheetSlice = createSlice({
  name: "entitySheet",
  initialState: detailSheetSliceInitialState,
  reducers: {
    open: (
      state,
      action: PayloadAction<{
        entity?: Entity
        entityRef?: PlaceRef
        target?: EntitySheetTarget
        trigger?: EntitySheetTriggerType
        itineraryEvent?: ItineraryEvent
        messageId?: Message["id"] | undefined
        scheduleStartAt?: number
        priceQuoteRef?: string
        placeSearchParams?: EntitySheetPlaceSearchParams
        entitySheetTrackingContext?: EntitySheetTrackingContext
        addTarget?: PlaceAddTarget
      }>
    ) => {
      const {
        entity,
        entityRef: entityRefParam,
        target,
        trigger,
        itineraryEvent,
        messageId,
        scheduleStartAt,
        priceQuoteRef,
        placeSearchParams,
        entitySheetTrackingContext,
        addTarget,
      } = action.payload

      const entityRef = entity?.entity_ref ?? entityRefParam
      assert(entityRef, "open requires entity or entityRef")

      const previousEntity = state.entity
      const isNewEntity =
        previousEntity?.entity_ref !== entityRef || !state.open

      if (isNewEntity) {
        state.roomsView = undefined
        addEntityToHistory(
          state,
          entityRef,
          entity,
          scheduleStartAt,
          messageId,
          priceQuoteRef,
          placeSearchParams,
          addTarget === "trip" ? undefined : addTarget
        )
      } else if (state.history.currentIndex >= 0) {
        const currentEntry = state.history.stack[state.history.currentIndex]
        if (currentEntry.kind === "entity") {
          currentEntry.scheduleStartAt = scheduleStartAt
          currentEntry.messageId = messageId
          currentEntry.priceQuoteRef = priceQuoteRef
          currentEntry.placeSearchParams = placeSearchParams
        }
      }

      state.entity = entity
      state.target = target
      state.trigger = trigger || "click"
      state.itineraryEvent = itineraryEvent
      state.messageId = messageId
      state.scheduleStartAt = scheduleStartAt
      state.priceQuoteRef = priceQuoteRef
      state.placeSearchParams = placeSearchParams
      state.open = true
      state.predatesQuestion = false
      state.entitySheetTrackingContext =
        entitySheetTrackingContext || AmplitudeEmptyValue

      if (isNewEntity && isEmbedded() && entity) {
        if (entityIsPlace(entity)) {
          void (async () => {
            sendAnalyticsEvent({
              name: "ViewPlaceDetailSlideOut",
              properties: {
                ...placeToAmplitudeProperties(entity),
                context: entitySheetTrackingContext,
              },
            })
          })()
        } else if (entityIsEventActor(entity)) {
          sendAnalyticsEvent({
            name: "ViewEventActorDetailSlideOut",
            properties: {
              event_actor: eventActorToAmplitudeProperties(entity),
              context: entitySheetTrackingContext,
            },
          })
        }
      }
    },

    close: (state) => {
      Object.assign(state, detailSheetSliceInitialState)
    },

    openAgentRunReport: (
      state,
      action: PayloadAction<{ runRef: AgentRunRef; agentName?: string }>
    ) => {
      const { runRef, agentName } = action.payload
      const currentEntry =
        state.history.currentIndex >= 0
          ? state.history.stack[state.history.currentIndex]
          : undefined
      const isSamePage =
        currentEntry?.kind === "agent_run_report" &&
        currentEntry.runRef === runRef

      if (!isSamePage) {
        pushHistoryEntry(state, {
          kind: "agent_run_report",
          runRef,
          agentName,
        })
      }

      clearEntityPageState(state)
      state.open = true
      state.predatesQuestion = false
      state.trigger = "click"
    },

    openActionPillAnswer: (
      state,
      action: PayloadAction<{ pillRef: string; label?: string }>
    ) => {
      const currentEntry = state.history.stack[state.history.currentIndex]
      if (
        currentEntry?.kind !== "action_pill_answer" ||
        currentEntry.pillRef !== action.payload.pillRef
      ) {
        pushHistoryEntry(state, {
          kind: "action_pill_answer",
          ...action.payload,
        })
      } else if (action.payload.label) {
        currentEntry.label = action.payload.label
      }
      clearEntityPageState(state)
      state.open = true
      state.predatesQuestion = false
      state.trigger = "click"
    },

    openEphemeralLocationAnswer: (
      state,
      action: PayloadAction<{
        answerKey: string
        locationRef: string
        ribbonRef?: string
        label: string
      }>
    ) => {
      const currentEntry = state.history.stack[state.history.currentIndex]
      if (
        currentEntry?.kind !== "ephemeral_location_answer" ||
        currentEntry.answerKey !== action.payload.answerKey
      ) {
        pushHistoryEntry(state, {
          kind: "ephemeral_location_answer",
          ...action.payload,
        })
      } else {
        Object.assign(currentEntry, action.payload)
      }
      clearEntityPageState(state)
      state.open = true
      state.predatesQuestion = false
      state.trigger = "click"
    },

    openEphemeralPlaceQuestionAnswer: (
      state,
      action: PayloadAction<{
        answerKey: string
        entityRef: string
        question: string
        label: string
      }>
    ) => {
      const currentEntry = state.history.stack[state.history.currentIndex]
      if (
        currentEntry?.kind !== "ephemeral_place_question" ||
        currentEntry.answerKey !== action.payload.answerKey
      ) {
        pushHistoryEntry(state, {
          kind: "ephemeral_place_question",
          ...action.payload,
        })
      } else {
        Object.assign(currentEntry, action.payload)
      }
      clearEntityPageState(state)
      state.open = true
      state.trigger = "click"
    },

    openCollaboratorChat: (
      state,
      action: PayloadAction<Omit<CollaboratorChatHistoryEntry, "kind">>
    ) => {
      const currentEntry = state.history.stack[state.history.currentIndex]
      if (
        currentEntry?.kind !== "collaborator_chat" ||
        currentEntry.chatId !== action.payload.chatId
      ) {
        pushHistoryEntry(state, {
          kind: "collaborator_chat",
          ...action.payload,
        })
      }
      clearEntityPageState(state)
      state.open = true
      state.trigger = "click"
    },

    openIdeas: (
      state,
      action: PayloadAction<{
        target: IdeasTarget
        label: string
      }>
    ) => {
      const currentEntry = state.history.stack[state.history.currentIndex]
      if (
        currentEntry?.kind !== "ideas" ||
        ideasTargetRef(currentEntry.target) !==
          ideasTargetRef(action.payload.target)
      ) {
        pushHistoryEntry(state, {
          kind: "ideas",
          ...action.payload,
        })
      } else {
        Object.assign(currentEntry, action.payload)
      }
      clearEntityPageState(state)
      state.open = true
      state.trigger = "click"
    },

    openCollection: (
      state,
      action: PayloadAction<{ collectionRef: CollectionRef }>
    ) => {
      const currentEntry = state.history.stack[state.history.currentIndex]
      if (
        currentEntry?.kind !== "collection" ||
        currentEntry.collectionRef !== action.payload.collectionRef
      ) {
        pushHistoryEntry(state, { kind: "collection", ...action.payload })
      }
      clearEntityPageState(state)
      state.open = true
      state.trigger = "click"
    },

    openSearchResult: (
      state,
      action: PayloadAction<{
        searchResult: WebSearchResult
        places?: PlaceSummary[]
        eventActors?: EventActorSummary[]
        messageId?: number
        webSearchResultId?: string
      }>
    ) => {
      const {
        searchResult,
        places,
        eventActors,
        messageId,
        webSearchResultId,
      } = action.payload
      const entryKey = searchResultEntryKey({
        searchResult,
        webSearchResultId,
      })
      const currentEntry =
        state.history.currentIndex >= 0
          ? state.history.stack[state.history.currentIndex]
          : undefined
      const isSamePage =
        currentEntry?.kind === "search_result" &&
        historyEntryKey(currentEntry) === entryKey

      if (!isSamePage) {
        pushHistoryEntry(state, {
          kind: "search_result",
          searchResult,
          places,
          eventActors,
          messageId,
          webSearchResultId,
        })
      }

      clearEntityPageState(state)
      state.open = true
      state.predatesQuestion = false
      state.trigger = "click"
    },

    openTrip: (state, action: PayloadAction<{ tripRef: TripRef }>) => {
      openTripPage(state, action.payload.tripRef)
    },

    openAfterStream: (state, action: PayloadAction<DelayedPage>) => {
      state.delayedPage = action.payload
    },

    navigateToHistoryIndex: (state, action: PayloadAction<number>) => {
      const index = action.payload
      if (index >= 0 && index < state.history.stack.length) {
        state.history.isNavigating = true
        state.history.currentIndex = index
        state.roomsView = undefined
      }
    },

    showRoomsView: (state, action: PayloadAction<HotelRoomsViewParams>) => {
      state.roomsView = action.payload
    },

    hideRoomsView: (state) => {
      state.roomsView = undefined
    },

    updateHistoryEntry: (
      state,
      action: PayloadAction<{
        scroll?: number
        extras?: {
          mentions: Mention[]
          stats: Stats
        }
      }>
    ) => {
      const { scroll, extras } = action.payload

      if (state.history.currentIndex >= 0) {
        const currentEntry = state.history.stack[state.history.currentIndex]

        if (scroll !== undefined) {
          currentEntry.scroll = scroll
        }
        if (extras && currentEntry.kind === "entity") {
          currentEntry.extras = extras
        }
      }

      state.history.isNavigating = false
    },

    setCurrEntityInspiration: (
      state,
      action: PayloadAction<Inspiration | undefined>
    ) => {
      state.entity = action.payload
    },

    setEntity: (state, action: PayloadAction<Entity | undefined>) => {
      state.entity = action.payload
    },

    hydrateEntity: (state, action: PayloadAction<Entity>) => {
      const hydratedEntity = action.payload
      state.entity = hydratedEntity

      if (state.history.currentIndex >= 0) {
        const currentEntry = state.history.stack[state.history.currentIndex]
        if (
          currentEntry.kind === "entity" &&
          currentEntry.entityRef === hydratedEntity.entity_ref
        ) {
          currentEntry.entity = hydratedEntity
        }
      }
    },

    setPriceQuoteRef: (state, action: PayloadAction<string | undefined>) => {
      state.priceQuoteRef = action.payload

      if (state.history.currentIndex >= 0) {
        const currentEntry = state.history.stack[state.history.currentIndex]
        if (currentEntry.kind === "entity") {
          currentEntry.priceQuoteRef = action.payload
        }
      }
    },

    appendUnloadedInspirationItem: (
      state,
      action: PayloadAction<{
        index: number
        items: InspirationItemSummary[]
      }>
    ) => {
      if (
        state.entity &&
        entityIsGuide(state.entity) &&
        isDetailedEntity(state.entity) &&
        state.entity.items
      ) {
        const index = action.payload.index
        const curr_fetch_count = state.entity.fetch_count ?? 0

        const itemsToAppend =
          index < curr_fetch_count
            ? action.payload.items.slice(curr_fetch_count)
            : action.payload.items

        state.entity.items = state.entity.items.concat(itemsToAppend)

        state.entity.fetch_count = curr_fetch_count + itemsToAppend.length
      }
    },

    updateDetailedInspirationItem: (
      state,
      action: PayloadAction<DetailedInspirationItem>
    ) => {
      if (
        state.entity &&
        entityIsGuide(state.entity) &&
        isDetailedEntity(state.entity) &&
        state.entity?.items
      ) {
        // Next, find where this is in the list
        const index = state.entity.items.findIndex(
          (inspirationItem) => inspirationItem.id === action.payload.id
        )
        if (index !== -1) {
          // Remove and then add to same position (update)
          state.entity.items.splice(index, 1, action.payload)
        }
      }
    },

    clearItineraryEvent: (state) => {
      state.itineraryEvent = undefined
    },

    restoreHistoryContext: (
      state,
      action: PayloadAction<{
        scheduleStartAt?: number
        messageId?: Message["id"]
        priceQuoteRef?: string
        placeSearchParams?: EntitySheetPlaceSearchParams
      }>
    ) => {
      const { scheduleStartAt, messageId, priceQuoteRef, placeSearchParams } =
        action.payload
      state.scheduleStartAt = scheduleStartAt
      state.messageId = messageId
      state.priceQuoteRef = priceQuoteRef
      state.placeSearchParams = placeSearchParams
    },

    resetState: (state) => {
      Object.assign(state, detailSheetSliceInitialState)
    },

    setExpanded: (state, action: PayloadAction<boolean>) => {
      state.expanded = action.payload
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(
        mapHoverSlice.actions.updatePlaceIfHighlighted,
        (state, action) => {
          const newPlace = action.payload
          if (state.entity?.entity_ref === newPlace.entity_ref) {
            state.entity = newPlace
          }
        }
      )
      // A new question starts a fresh turn: whatever is open now was not opened for it
      .addCase(userSlice.actions.addMessage, (state, action) => {
        if (action.payload.message.type === "customer") {
          state.predatesQuestion = state.open
        }
      })
      .addCase(userSlice.actions.stopMessageStreaming, (state) => {
        const delayedPage = state.delayedPage
        if (!delayedPage) return

        if (delayedPage.kind === "trip") {
          openTripPage(state, delayedPage.tripRef)
        } else {
          const { entity } = delayedPage
          addEntityToHistory(state, entity.entity_ref, entity)
          state.entity = entity
          state.open = true
          state.predatesQuestion = false
        }
        state.trigger = "auto"
        state.delayedPage = undefined
      })
      .addCase(userSlice.actions.stopMessageStreamingWithError, (state) => {
        state.delayedPage = undefined
      })
      .addCase(userSlice.actions.stopMessageStreamingAndDisable, (state) => {
        state.delayedPage = undefined
      })
      .addCase(userSlice.actions.stopMessageStreamingWithCancel, (state) => {
        state.delayedPage = undefined
      })
      // Embedded view switches leave the trip they were showing, so drop any
      // trip-rooted history. Sheets opened outside a trip survive the switch.
      .addCase(embeddedSlice.actions.setView, (state) => {
        if (tripInStack(state.open, state.history)) {
          return detailSheetSliceInitialState
        }
      })
  },
})

export const {
  openTrip,
  openAfterStream,
  navigateToHistoryIndex,
  updateHistoryEntry,
  setCurrEntityInspiration,
  setEntity,
  hydrateEntity,
  setPriceQuoteRef,
  clearItineraryEvent,
  restoreHistoryContext,
  resetState,
  setExpanded,
  showRoomsView,
  hideRoomsView,
} = detailSheetSlice.actions

type OpenEntitySheetParams = {
  target?: EntitySheetTarget
  trigger?: EntitySheetTriggerType
  itineraryEvent?: ItineraryEvent
  messageId?: Message["id"] | undefined
  scheduleStartAt?: number
  priceQuoteRef?: string
  placeSearchParams?: EntitySheetPlaceSearchParams
  entitySheetTrackingContext: EntitySheetTrackingContext
  addTarget?: PlaceAddTarget
} & (
  | { entity: Entity; entityRef?: never }
  | { entityRef: PlaceRef; entity?: never }
)

async function hydratePlaceRef(
  placeRef: PlaceRef,
  dispatch: AppDispatch,
  getState: () => RootState
) {
  try {
    const { scheduleStartAt, messageId } = getState().detailSheetSlice
    const eventParams =
      scheduleStartAt !== undefined ? { at: scheduleStartAt } : undefined
    const persistedMessageId = messageIdForPlaceApi(messageId)

    const place = persistedMessageId
      ? await dispatch(
          placeApi.endpoints.getDetailedPlaceWithMessage.initiate({
            placeRef,
            messageId: persistedMessageId,
            eventParams,
          })
        ).unwrap()
      : await dispatch(
          placeApi.endpoints.getDetailedPlace.initiate({
            placeRef,
            eventParams,
          })
        ).unwrap()

    if (selectEntitySheetEntityRef(getState()) === placeRef) {
      dispatch(detailSheetSlice.actions.hydrateEntity(place))
    }
  } catch {
    // Sheet stays open with loading spinner until user closes or navigates away.
  }
}

export const openEntitySheet = createAsyncThunk<
  void,
  OpenEntitySheetParams,
  { dispatch: AppDispatch; state: RootState }
>("entitySheet/openSheet", async (params, { dispatch, getState }) => {
  // cancel any pending cleanup from a previous closeDetailSheet to prevent
  // a race condition where the cleanup fires after new data is loaded
  cancelPendingCloseCleanup()

  const { entity, entityRef } = params

  snapshotSheetScroll(dispatch)
  dispatch(detailSheetSlice.actions.open(params))

  if (entityRef && !entity) {
    void hydratePlaceRef(entityRef, dispatch, getState)
  }
})

export function cancelPendingCloseCleanup() {
  if (closeCleanupTimeoutId) {
    clearTimeout(closeCleanupTimeoutId)
    closeCleanupTimeoutId = null
  }
}

export const closeDetailSheet = createAsyncThunk<
  void,
  { resetPlace?: boolean } | void,
  { dispatch: AppDispatch; state: RootState }
>("entitySheet/closeSheet", async (params, { dispatch, getState }) => {
  const resetPlace = params?.resetPlace !== false
  const resetTrip = selectTripInSheetStack(getState())
  dispatch(detailSheetSlice.actions.close())

  cancelPendingCloseCleanup()

  // Standalone place detail pages own placeSlice search params (dates /
  // travelers). Resetting them on close would blank the page's booking CTA.
  // Trip tab and highlight state is shared with pages outside the sheet, so
  // only reset it when a trip was part of what just closed.
  if (!resetPlace && !resetTrip) return

  closeCleanupTimeoutId = setTimeout(() => {
    if (resetPlace) {
      dispatch(placeSlice.actions.reset())
    }
    if (resetTrip) {
      dispatch(tripSlice.actions.clearHighlightedEvent())
      dispatch(tripSlice.actions.setCurrentTab(TripTabItinerary))
    }
    closeCleanupTimeoutId = null
  }, DRAWER_ANIMATION_DURATION)
})

/** Closes the sheet when the user is working inside a trip, leaving unrelated pages open */
export const closeDetailSheetIfTrip = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>("entitySheet/closeSheetIfTrip", async (_, { dispatch, getState }) => {
  if (selectTripInSheetStack(getState())) {
    await dispatch(closeDetailSheet())
  }
})

type ShowEntitySheetRoomsViewParams = {
  entitySheetTrackingContext: EntitySheetTrackingContext
} & Omit<HotelRoomsViewParams, "placeRef"> &
  (
    | { place: Place; placeRef?: undefined }
    | { place?: undefined; placeRef: PlaceRef }
  )

export const showEntitySheetRoomsView = createAsyncThunk<
  void,
  ShowEntitySheetRoomsViewParams,
  { dispatch: AppDispatch; state: RootState }
>("entitySheet/showRoomsView", async (params, { dispatch, getState }) => {
  const {
    entitySheetTrackingContext,
    place: placeParam,
    placeRef: placeRefParam,
    ...roomsViewParams
  } = params
  const placeRef = placeParam?.entity_ref ?? placeRefParam
  assert(placeRef, "showEntitySheetRoomsView requires place or placeRef")

  const state = getState()
  const entitySheetOpen = selectDetailSheetOpen(state)
  const currentEntityRef = selectEntitySheetEntityRef(state)

  if (!entitySheetOpen || currentEntityRef !== placeRef) {
    if (placeParam) {
      await dispatch(
        openEntitySheet({
          entity: placeParam,
          priceQuoteRef: roomsViewParams.hotelQuoteRef ?? undefined,
          entitySheetTrackingContext,
        })
      )
    } else {
      await dispatch(
        openEntitySheet({
          entityRef: placeRef,
          priceQuoteRef: roomsViewParams.hotelQuoteRef ?? undefined,
          entitySheetTrackingContext,
        })
      )
    }
  }

  dispatch(
    detailSheetSlice.actions.showRoomsView({
      ...roomsViewParams,
      placeRef,
    })
  )
})

export const openAgentRunReportSheet = createAsyncThunk<
  void,
  { runRef: AgentRunRef; agentName?: string },
  { dispatch: AppDispatch; state: RootState }
>("detailSheet/openAgentRunReport", async (params, { dispatch, getState }) => {
  const user = selectSession(getState())?.user
  if (!user || user.provider === "anonymous") {
    const query = new URLSearchParams(mtLocation.search)
    query.set("ref", params.runRef)
    await dispatch(
      openAuthDialog({
        callbackUrl: `${mtLocation.pathname}?${query}`,
        setupTravelAssistantAfterSignup: false,
      })
    )
    return
  }

  cancelPendingCloseCleanup()

  snapshotSheetScroll(dispatch)
  dispatch(detailSheetSlice.actions.openAgentRunReport(params))
})

export const openActionPillAnswerSheet = createAsyncThunk<
  void,
  { pillRef: string; label?: string },
  { dispatch: AppDispatch }
>("detailSheet/openActionPillAnswer", async (params, { dispatch }) => {
  cancelPendingCloseCleanup()

  snapshotSheetScroll(dispatch)
  dispatch(detailSheetSlice.actions.openActionPillAnswer(params))
})

export const openEphemeralLocationAnswerSheet = createAsyncThunk<
  void,
  {
    answerKey: string
    locationRef: string
    ribbonRef?: string
    label: string
  },
  { dispatch: AppDispatch }
>("detailSheet/openEphemeralLocationAnswer", async (params, { dispatch }) => {
  cancelPendingCloseCleanup()

  snapshotSheetScroll(dispatch)
  dispatch(detailSheetSlice.actions.openEphemeralLocationAnswer(params))
})

export const openEphemeralPlaceQuestionAnswerSheet = createAsyncThunk<
  void,
  {
    answerKey: string
    entityRef: string
    question: string
    label: string
  },
  { dispatch: AppDispatch }
>("detailSheet/openEphemeralPlaceQuestion", async (params, { dispatch }) => {
  cancelPendingCloseCleanup()

  snapshotSheetScroll(dispatch)
  dispatch(detailSheetSlice.actions.openEphemeralPlaceQuestionAnswer(params))
})

export const openCollaboratorChatSheet = createAsyncThunk<
  void,
  Omit<CollaboratorChatHistoryEntry, "kind">,
  { dispatch: AppDispatch }
>("detailSheet/openCollaboratorChat", async (params, { dispatch }) => {
  cancelPendingCloseCleanup()

  snapshotSheetScroll(dispatch)
  dispatch(detailSheetSlice.actions.openCollaboratorChat(params))
})

export const openIdeasSheet = createAsyncThunk<
  void,
  { target: IdeasTarget; label: string },
  { dispatch: AppDispatch }
>("detailSheet/openIdeas", async (params, { dispatch }) => {
  cancelPendingCloseCleanup()

  snapshotSheetScroll(dispatch)
  dispatch(detailSheetSlice.actions.openIdeas(params))
})

export const openCollectionSheet = createAsyncThunk<
  void,
  { collectionRef: CollectionRef },
  { dispatch: AppDispatch }
>("detailSheet/openCollection", async (params, { dispatch }) => {
  cancelPendingCloseCleanup()

  snapshotSheetScroll(dispatch)
  dispatch(detailSheetSlice.actions.openCollection(params))
})

export const openWebEventSheetByRef = createAsyncThunk<
  void,
  { ref: string },
  { dispatch: AppDispatch }
>("detailSheet/openWebEventSheetByRef", async ({ ref }, { dispatch }) => {
  cancelPendingCloseCleanup()

  snapshotSheetScroll(dispatch)

  let searchResult: WebSearchResult | undefined
  let places: PlaceSummary[] | undefined
  let eventActors: EventActorSummary[] | undefined
  try {
    const lookup = await dispatch(
      webEventsApi.endpoints.getWebEvents.initiate({ refs: [ref] })
    ).unwrap()
    searchResult = lookup.events.find((event) => event.ref === ref)
    places = lookup.places
    eventActors = lookup.eventActors
  } catch {
    // fall through to the unavailable toast
  }

  if (searchResult) {
    dispatch(
      detailSheetSlice.actions.openSearchResult({
        searchResult,
        places,
        eventActors,
      })
    )
  } else {
    toastManager.add({
      type: "error",
      description: "This event is no longer available.",
    })
  }
})

export const openSearchResultSheet = createAsyncThunk<
  void,
  {
    searchResult: WebSearchResult
    places?: PlaceSummary[]
    eventActors?: EventActorSummary[]
    messageId?: number
    webSearchResultId?: string
  },
  { dispatch: AppDispatch }
>("detailSheet/openSearchResult", async (params, { dispatch }) => {
  cancelPendingCloseCleanup()

  snapshotSheetScroll(dispatch)
  dispatch(detailSheetSlice.actions.openSearchResult(params))
})

export const navigateToHistoryIndexThunk = createAsyncThunk<
  void,
  number,
  { dispatch: AppDispatch; state: RootState }
>(
  "entitySheet/navigateToHistoryIndex",
  async (index, { dispatch, getState }) => {
    const state = getState()
    const history = state.detailSheetSlice.history

    if (index < 0 || index >= history.stack.length) {
      return
    }

    const entry = history.stack[index]

    if (entry.kind === "trip" && entry.tripRef !== selectTripRef(state)) {
      const cachedTrip = tripsApi.endpoints.getTrip.select(entry.tripRef)(
        state
      ).data
      void dispatch(initTrip(cachedTrip ?? entry.tripRef))
    }

    if (entry.kind !== "entity") {
      dispatch(detailSheetSlice.actions.navigateToHistoryIndex(index))
      dispatch(detailSheetSlice.actions.setEntity(undefined))
      dispatch(detailSheetSlice.actions.restoreHistoryContext({}))
      return
    }

    const currentEntity = state.detailSheetSlice.entity

    // Try to get entity from RTK Query cache
    let entity: Entity | undefined

    if (isPlaceRef(entry.entityRef)) {
      const placeRef = entry.entityRef
      const eventParams = entry.scheduleStartAt
        ? { at: entry.scheduleStartAt }
        : undefined

      const persistedMessageId = messageIdForPlaceApi(entry.messageId)

      // Try with messageId first if present
      if (persistedMessageId) {
        const cachedPlaceWithMessage =
          placeApi.endpoints.getDetailedPlaceWithMessage.select({
            placeRef,
            messageId: persistedMessageId,
            eventParams,
          })(state)?.data
        if (cachedPlaceWithMessage) {
          entity = cachedPlaceWithMessage
        }
      }

      // Then try without messageId
      if (!entity) {
        const cachedPlace = placeApi.endpoints.getDetailedPlace.select({
          placeRef,
          eventParams,
        })(state)?.data
        if (cachedPlace) {
          entity = cachedPlace
        }
      }

      // Also try without eventParams in case it was cached that way
      if (!entity && eventParams) {
        const cachedPlaceNoParams = placeApi.endpoints.getDetailedPlace.select({
          placeRef,
        })(state)?.data
        if (cachedPlaceNoParams) {
          entity = cachedPlaceNoParams
        }
      }
    } else if (isInspirationRef(entry.entityRef)) {
      const cachedInspiration =
        inspirationApi.endpoints.getDetailedInspiration.select({
          inspirationRef: entry.entityRef,
        })(state)?.data
      if (cachedInspiration) {
        entity = cachedInspiration
      }
    } else if (isEventActorRef(entry.entityRef)) {
      const cachedEventActor =
        eventActorApi.endpoints.getDetailedEventActor.select({
          eventActorRef: entry.entityRef,
        })(state)?.data
      if (cachedEventActor) {
        entity = cachedEventActor
      }
    }

    // Fallback: use current entity if it matches the ref
    if (!entity && currentEntity?.entity_ref === entry.entityRef) {
      entity = currentEntity
    }

    // Entity-ref-only opens have no snapshot until hydration completes.
    if (!entity && isPlaceRef(entry.entityRef)) {
      dispatch(detailSheetSlice.actions.navigateToHistoryIndex(index))
      dispatch(
        detailSheetSlice.actions.restoreHistoryContext({
          scheduleStartAt: entry.scheduleStartAt,
          messageId: entry.messageId,
          priceQuoteRef: entry.priceQuoteRef,
          placeSearchParams: entry.placeSearchParams,
        })
      )
      dispatch(detailSheetSlice.actions.setEntity(undefined))
      void hydratePlaceRef(entry.entityRef, dispatch, getState)
      return
    }

    // Final fallback: use the entity snapshot saved on the history entry.
    // This is critical for guides — their RTK Query cache key includes
    // `batchSize` which we don't reliably know here, so cache lookups above
    // often miss. Without this, `setEntity` wouldn't fire, the URL wouldn't
    // update, and the UI would appear stuck on the current entity.
    if (!entity && entry.entity) {
      entity = entry.entity
    }

    // Navigate to the history index
    dispatch(detailSheetSlice.actions.navigateToHistoryIndex(index))

    // Set entity if we found one; component will fetch detailed version if needed
    if (entity) {
      dispatch(detailSheetSlice.actions.setEntity(entity))
    }

    // Restore scheduleStartAt and messageId for the entity sheet to use
    dispatch(
      detailSheetSlice.actions.restoreHistoryContext({
        scheduleStartAt: entry.scheduleStartAt,
        messageId: entry.messageId,
        priceQuoteRef: entry.priceQuoteRef,
        placeSearchParams: entry.placeSearchParams,
      })
    )
  }
)

export const appendItemsAndReturnInspiration = createAsyncThunk<
  { inspiration: Inspiration | null },
  {
    detailedInspiration: DetailedInspiration
    index: number
    items: InspirationItemSummary[]
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "entity/appendItemsAndReturnEntity",
  ({ detailedInspiration, index, items }, { dispatch, getState }) => {
    dispatch(
      detailSheetSlice.actions.setCurrEntityInspiration(detailedInspiration)
    )
    dispatch(
      detailSheetSlice.actions.appendUnloadedInspirationItem({ index, items })
    )
    const entity = getState().detailSheetSlice.entity
    const inspiration =
      entityIsGuide(entity) && isDetailedEntity(entity)
        ? (entity as Inspiration)
        : null

    return { inspiration }
  }
)

export const updateItemAndReturnInspiration = createAsyncThunk<
  { inspiration: Inspiration | null },
  {
    item: DetailedInspirationItem
  },
  { dispatch: AppDispatch; state: RootState }
>("entity/appendItemsAndReturnEntity", ({ item }, { dispatch, getState }) => {
  dispatch(detailSheetSlice.actions.updateDetailedInspirationItem(item))
  const entity = getState().detailSheetSlice.entity
  const inspiration =
    entityIsGuide(entity) && isDetailedEntity(entity)
      ? (entity as Inspiration)
      : null

  return { inspiration }
})

export default detailSheetSlice
